import { readonly, ref } from 'vue';
import { defineStore } from 'pinia';
import { NEVER } from 'rxjs';
import { cloneDeep } from 'lodash';

import { SmartSearchService } from '@apps/main/modules/search/smartSearchService';
import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';
import { adaptRecipeIngredientsToItinerarySearchCriteria } from '@apps/main/modules/criteria/itinerarySearchCriteria/itinerariesCriteria';
import {
  usePaxStore,
  useTripStore,
  useRecipeStore,
  usePreferredTimeStore,
  useCriteriaStore,
} from '@apps/main/new-desktop/core/stores';
import { ItinerarySearchResults } from '@apps/main/modules/tripPlannerModel/itinerarySearchResults';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients';
import { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  IngredientId,
  ItinerarySearchCriteria,
  ItinerarySearchUserCriteria,
  OverviewStaticInfoResult,
} from '@libs/trip-planner-interfaces';

const SEARCH_SERVICE = new SmartSearchService();

export const useAccommodationUpsellStore = defineStore(
  'AccommodationUpsell',
  () => {
    const paxStore = usePaxStore();
    const preferredTimeStore = usePreferredTimeStore();
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();
    const criteriaStore = useCriteriaStore();

    // State
    const itineraryDatesRef = ref<DateWithItinerary[]>([]);
    const itineraryStaticInfo = ref<OverviewStaticInfoResult>({
      destinationStaticInfo: {},
      subIngredientsStaticInfo: {},
      heartedHotelsStaticInfo: {},
    });

    // Actions
    function loadSuggestions(ingredientId: IngredientId): void {
      itineraryDatesRef.value = [];
      const ownStayIngredient = recipeStore.getOwnStayIngredient(ingredientId);
      const hotelIngredient = promoteOwnStayIngredientToHotelIngredient(
        cloneDeep(ownStayIngredient)
      );
      const recipe = [hotelIngredient];

      const dates = {
        startDate: preferredTimeStore.startSearchDate,
        endDate: preferredTimeStore.endSearchDate,
        selectedDate: preferredTimeStore.preferredDay,
      };

      const userCriteria: ItinerarySearchUserCriteria = {
        ingredients: adaptRecipeIngredientsToItinerarySearchCriteria(recipe),
        pax: paxStore.pax.rooms,
        endSearchDate: dates.endDate,
        startSearchDate: dates.startDate,
        selectedDate: dates.selectedDate,
        sessionId: tripStore.sessionId,
      };
      const criteria: ItinerarySearchCriteria = {
        userCriteria,
        businessCriteria: criteriaStore.businessCriteria,
      };
      const results = new ItinerarySearchResults(
        SEARCH_SERVICE,
        criteria,
        NEVER
      );
      results.itineraries.subscribe((i) => {
        const itinerary = { date: i.date, itinerary: i };
        const allItineraries = [...itineraryDatesRef.value, itinerary];
        allItineraries.sort((a, b) => a.date.getTime() - b.date.getTime());
        itineraryDatesRef.value = [...allItineraries];
      });

      results.staticInfo.subscribe((staticInfo) => {
        itineraryStaticInfo.value = {
          destinationStaticInfo: {
            ...itineraryStaticInfo.value.destinationStaticInfo,
            ...staticInfo.destinationStaticInfo,
          },
          subIngredientsStaticInfo: {
            ...itineraryStaticInfo.value.subIngredientsStaticInfo,
            ...staticInfo.subIngredientsStaticInfo,
          },
          heartedHotelsStaticInfo: {
            ...itineraryStaticInfo.value.heartedHotelsStaticInfo,
            ...staticInfo.heartedHotelsStaticInfo,
          },
        };
      });
    }

    function promoteOwnStayIngredientToHotelIngredient(
      ownStayIngredient: OwnStayIngredient
    ): HotelIngredient {
      const hotelIngredient = new HotelIngredient(ownStayIngredient.searchTerm);
      hotelIngredient.numberOfNights = ownStayIngredient.numberOfNights;
      hotelIngredient.subIngredients = [];

      if (ownStayIngredient.displayText) {
        hotelIngredient.displayText = ownStayIngredient.displayText;
      }
      return hotelIngredient;
    }

    return {
      // State
      itineraryDatesRef: readonly(itineraryDatesRef),
      itineraryStaticInfo: readonly(itineraryStaticInfo),

      // Actions
      loadSuggestions,
    };
  }
);
